// @ts-check
import { graphql } from 'gatsby';
import React from 'react';
import Link from '~/components/Common/Link';
import GatsbyBackgroundImage from './images/GatsbyBackgroundImage';
import SvgImage from './images/SvgImage';

import './FeaturedItemsWrapper.scss';

export const FeaturedItemBlock_REFERENCES = [
	'featured_item_block.single_item.background_image',
	'featured_item_block.single_item.mobile_background_image',
];

// Export query with fragment - it will be available globally.
export const query = graphql`
	fragment FeaturedItemBlockFragment on Ozk_pageContent {
		featured_item_block {
			single_item {
				heading
				image_as_heading {
					alt_text
					left_icon {
						...AssetFragment
					}
					image {
						...AssetFragment
					}
					mobile_image {
						...AssetFragment
					}
				}
				subheading
				action_text
				link
				open_in_new_tab
				overlay_opacity

				background_image {
					remoteToLocalImage {
						childImageSharp {
							desktop: gatsbyImageData(quality: 100, layout: CONSTRAINED)
						}
					}
					...BynderImageFragment
				}
				mobile_background_image {
					remoteToLocalImage {
						childImageSharp {
							mobile: gatsbyImageData(quality: 100, layout: CONSTRAINED)
						}
					}
					...BynderImageFragment
				}
			}
		}
	}
`;

/**
 * @typedef SingleFeatureItem
 * @property {string} action_text
 * @property {import('../CSTypes').BynderImageOptions[]} background_image
 * @property {import('../CSTypes').BynderImageOptions[]} mobile_background_image
 * @property {string} [heading]
 * @property {import('../CSTypes').ImageAsHeadingOptions[]} [image_as_heading]
 * @property {string} link
 * @property {boolean} open_in_new_tab
 * @property {string} [subheading]
 * @property {number} [overlay_opacity=0.25]
 */
/**
 * @typedef FeaturedItemsOptions
 * @property {SingleFeatureItem[]} single_item
 */
/**
 * @typedef FeaturedItemWrapperProps
 * @property {import('~/contentstack/types').ModelWithLivePreviewAttr<FeaturedItemsOptions>} options
 */
/** @type {React.FC<FeaturedItemWrapperProps>} */
const FeaturedItemsWrapper = ({ options }) => {
	const withBackgrounds = (options.single_item ?? []).filter((i) => !!i.background_image?.[0]);

	if (!withBackgrounds.length) {
		return null;
	}

	return (
		<div className="cs-featured-items container">
			<div className="row">
				{withBackgrounds.map((item, i) => (
					<SingleFeatureItem item={item} key={i} />
				))}
			</div>
		</div>
	);
};

/** @type {React.FC<import('~/contentstack/types').ModelWithLivePreviewAttr<{ item: SingleFeatureItem }>>} */
const SingleFeatureItem = ({ item }) => {
	const imageAsHeading = item.image_as_heading?.[0];

	const hasHeadingImage = imageAsHeading?.image;
	const hasHeading = !hasHeadingImage && item.heading;
	const hasHeadingLeftIcon = !hasHeadingImage && imageAsHeading?.left_icon;

	return (
		<Link className="featured-item col-md-4" to={item.link}>
			<GatsbyBackgroundImage
				className="featured-item-content"
				keyOfChildImageSharp="desktop"
				option={item.background_image[0]}
				mobileOption={item.mobile_background_image?.[0]}
			>
				<div className="image-background-overlay" style={{ opacity: item.overlay_opacity ?? 0.25 }} />

				{/* Heading section, added and configured when necessary. */}
				<div className="heading-container">
					{/* Add the ImageAsHeading>Image as priority. */}
					{hasHeadingImage && (
						<SvgImage
							className="image-as-heading desktop"
							options={{
								alt_text: imageAsHeading.alt_text,
								svg: imageAsHeading.image,
								mobileSvg: imageAsHeading.mobile_image,
							}}
							{...imageAsHeading.image.$?.url}
						/>
					)}

					{/* Add icon if present. */}
					{hasHeadingLeftIcon && (
						<SvgImage
							className="image-as-heading-left-icon"
							options={{
								alt_text: imageAsHeading.alt_text,
								svg: imageAsHeading.left_icon,
							}}
							{...imageAsHeading.left_icon?.$?.url}
						/>
					)}

					{/* Only show heading when image not present */}
					{hasHeading && <h2 className="title">{item.heading}</h2>}
				</div>

				{!!item.subheading && <p className="sub-title">{item.subheading}</p>}

				<div className="featured-item-cta-text right-arrow--after">{item.action_text}</div>
			</GatsbyBackgroundImage>
		</Link>
	);
};

export default FeaturedItemsWrapper;
